import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './DistDiscountsConfig.css';

export default function DistPoMappingConfig() {
  const navigate = useNavigate();

  const [mappings,      setMappings]      = useState([]); // [{tag, distributor}]
  const [distCustomers, setDistCustomers] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState(null);

  const [newTag,         setNewTag]         = useState('');
  const [newDistributor, setNewDistributor] = useState('');

  function reload() {
    setLoading(true);
    Promise.all([
      axios.get('/api/dist-orders/config/po-mapping'),
      axios.get('/api/dist-orders/dist-customers'),
    ]).then(([mapRes, custRes]) => {
      setMappings(mapRes.data.mappings || []);
      setDistCustomers(custRes.data || []);
      setLoading(false);
    }).catch(err => {
      const status = err.response?.status;
      const detail = err.response?.data?.error;
      setError(
        status === 401
          ? 'Failed to load PO mapping — you may have been signed out. Try refreshing the page.'
          : `Failed to load PO mapping${status ? ` (${status})` : ''}${detail ? `: ${detail}` : ''}`
      );
      setLoading(false);
    });
  }

  useEffect(() => { reload(); }, []);

  // Distinct secondary tags (anything besides DIST) seen on live distributors,
  // with an example account name — same "tags seen" hint as the Discounts page.
  const seenTags = {};
  distCustomers.forEach(c => {
    (c.tags || []).forEach(t => {
      if (t.toUpperCase() !== 'DIST' && !seenTags[t]) seenTags[t] = c.display;
    });
  });
  const mappedTags   = new Set(mappings.map(m => (m.tag || '').toUpperCase()));
  const unmappedTags = Object.keys(seenTags).filter(t => !mappedTags.has(t.toUpperCase()));

  function upsertMapping(e) {
    e.preventDefault();
    if (!newTag.trim() || !newDistributor.trim()) return;
    setSaving(true);
    axios.post('/api/dist-orders/config/po-mapping', {
      tag: newTag.trim(), distributor: newDistributor.trim(),
    }).then(r => {
      setMappings(r.data.mappings || []);
      setNewTag(''); setNewDistributor('');
      setSaving(false);
    }).catch(() => { setError('Failed to save mapping'); setSaving(false); });
  }

  function editDistributor(tag, distributor) {
    setSaving(true);
    axios.post('/api/dist-orders/config/po-mapping', { tag, distributor })
      .then(r => { setMappings(r.data.mappings || []); setSaving(false); })
      .catch(() => { setError('Failed to update mapping'); setSaving(false); });
  }

  function removeMapping(tag) {
    setSaving(true);
    axios.delete(`/api/dist-orders/config/po-mapping/${encodeURIComponent(tag)}`)
      .then(r => { setMappings(r.data.mappings || []); setSaving(false); })
      .catch(() => { setError('Failed to remove mapping'); setSaving(false); });
  }

  return (
    <div className="ddcfg-page">
      <div className="ddcfg-header">
        <button className="ddcfg-back" onClick={() => navigate('/draft-orders')}>
          ← Back
        </button>
        <div>
          <h1>PO Sheet Mapping</h1>
          <p className="ddcfg-muted">
            Every draft order created here also adds a row to the "PO" tab of the Distributor PO# tracking sheet,
            with an Invoice Due date set 31 days after the Shopify order date. The <strong>Distributor</strong> column
            in that sheet is filled in by matching the confirmed customer's Shopify tag below — the same secondary
            tag (besides DIST) used for discount rates. If a customer's tag has no mapping here, the row is still
            added with a blank Distributor and a warning is shown so it can be fixed manually.
          </p>
        </div>
      </div>

      {error && (
        <div className="ddcfg-error">
          {error} <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <p className="ddcfg-muted">Loading…</p>
      ) : (
        <>
          {unmappedTags.length > 0 && (
            <div className="ddcfg-section">
              <label className="ddcfg-label">Unmapped tags</label>
              <p className="ddcfg-muted-sm">
                These tags appear on live DIST-tagged customers but have no PO-sheet mapping yet — any draft order
                for one of them will land in the sheet with a blank Distributor.
              </p>
              <p className="ddcfg-muted-sm">
                {unmappedTags.map(t => (
                  <span key={t} className="ddcfg-tag-hint">{t} <em>({seenTags[t]})</em></span>
                ))}
              </p>
            </div>
          )}

          {/* Add mapping */}
          <div className="ddcfg-section">
            <label className="ddcfg-label">Add / update a mapping</label>
            <form className="ddcfg-add-form" onSubmit={upsertMapping}>
              <input
                className="ddcfg-input"
                placeholder="Shopify tag (e.g. AGD, GTS)"
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                disabled={saving}
              />
              <input
                className="ddcfg-input"
                placeholder="Distributor label in the PO sheet (e.g. Alliance)"
                value={newDistributor}
                onChange={e => setNewDistributor(e.target.value)}
                disabled={saving}
              />
              <button className="ddcfg-btn-primary" type="submit" disabled={saving || !newTag.trim() || !newDistributor.trim()}>
                Save
              </button>
            </form>
            {Object.keys(seenTags).length > 0 && (
              <p className="ddcfg-muted-sm">
                Tags currently seen on distributors: {Object.entries(seenTags).map(([t, name]) => (
                  <span key={t} className="ddcfg-tag-hint">{t} <em>({name})</em></span>
                ))}
              </p>
            )}
          </div>

          {/* Mappings table */}
          {mappings.length === 0 ? (
            <p className="ddcfg-muted">No mappings configured yet — every draft order will land in the sheet with a blank Distributor.</p>
          ) : (
            <div className="ddcfg-table-wrap">
              <table className="ddcfg-table">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Distributor (PO sheet label)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map(m => (
                    <tr key={m.tag}>
                      <td className="ddcfg-account-tag">{m.tag}</td>
                      <td>
                        <input
                          className="ddcfg-input"
                          defaultValue={m.distributor}
                          disabled={saving}
                          onBlur={e => {
                            if (e.target.value && e.target.value !== m.distributor) {
                              editDistributor(m.tag, e.target.value);
                            }
                          }}
                        />
                      </td>
                      <td>
                        <button className="ddcfg-remove" disabled={saving} onClick={() => removeMapping(m.tag)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
